import { useCallback, useEffect, useState } from 'react'
import BackOfficeAPIClient from "../../../clients/BackofficeAPIClient"
import { FieldOption, NouveauTemplateField, SousBlocOption } from "../../../types/ChampsObligatoires"

interface UseChampsObligatoiresAdminReturn {
    fields: FieldOption[]
    loadingFields: boolean
    createField: (label: string, code: string) => Promise<FieldOption>
    fetchSubBlocs: (blockId: number) => Promise<SousBlocOption[]>
    createSubBloc: (blockId: number, label: string) => Promise<{ id: number, label: string, block_id: number, rank: number }>
    createTemplateField: (payload: NouveauTemplateField) => Promise<void>
    updateTemplateFieldCore: (templateFieldId: number, isCore: boolean) => Promise<void>
    updateTemplateFieldDetails: (templateFieldId: number, label: string, subBlockId: number | null) => Promise<void>
    deleteTemplateField: (templateFieldId: number) => Promise<void>
}

/**
 * Administration du référentiel (BDREF3) : création de fields, de sous-blocs, et rattachement
 * de fields à un bloc (template_fields). Réservé aux utilisateurs selectup (voir adminRequired
 * côté API).
 */
const useChampsObligatoiresAdmin = (): UseChampsObligatoiresAdminReturn => {
    const apiClient = new BackOfficeAPIClient()

    const [fields, setFields] = useState<FieldOption[]>([])
    const [loadingFields, setLoadingFields] = useState<boolean>(true)

    const fetchFields = useCallback(async () => {
        setLoadingFields(true)
        try {
            const data = await apiClient.get<FieldOption[]>('/champsObligatoires/admin/fields')
            setFields(data)
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingFields(false)
        }
    }, [])

    useEffect(() => {
        fetchFields()
    }, [fetchFields])

    const createField = useCallback(async (label: string, code: string) => {
        const field = await apiClient.post<FieldOption>('/champsObligatoires/admin/fields', { label, code })
        setFields(prev => [...prev, field].sort((a, b) => a.label.localeCompare(b.label, 'fr')))
        return field
    }, [])

    const fetchSubBlocs = useCallback(async (blockId: number) => {
        return await apiClient.get<SousBlocOption[]>(`/champsObligatoires/admin/subBlocks?blockId=${blockId}`)
    }, [])

    const createSubBloc = useCallback(async (blockId: number, label: string) => {
        return await apiClient.post<{ id: number, label: string, block_id: number, rank: number }>('/champsObligatoires/admin/subBlocks', { blockId, label })
    }, [])

    const createTemplateField = useCallback(async (payload: NouveauTemplateField) => {
        await apiClient.post('/champsObligatoires/admin/templateFields', payload)
    }, [])

    const updateTemplateFieldCore = useCallback(async (templateFieldId: number, isCore: boolean) => {
        await apiClient.patch(`/champsObligatoires/admin/templateFields/${templateFieldId}`, { isCore })
    }, [])

    const updateTemplateFieldDetails = useCallback(async (templateFieldId: number, label: string, subBlockId: number | null) => {
        await apiClient.patch(`/champsObligatoires/admin/templateFields/${templateFieldId}/details`, { label, subBlockId })
    }, [])

    const deleteTemplateField = useCallback(async (templateFieldId: number) => {
        await apiClient.delete(`/champsObligatoires/admin/templateFields/${templateFieldId}`, undefined)
    }, [])

    return {
        fields,
        loadingFields,
        createField,
        fetchSubBlocs,
        createSubBloc,
        createTemplateField,
        updateTemplateFieldCore,
        updateTemplateFieldDetails,
        deleteTemplateField,
    }
}

export default useChampsObligatoiresAdmin
